import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ButtonPrimary, GlyphRight, IconSetting, ToggleSwitch } from '@americanexpress/dls-react';

import Card from 'src/components/atomic/atoms/card/Card';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import WideView from 'src/components/atomic/layouts/wideView/WideView';

import LiteralsService from 'src/services/literals/LiteralsService';
import { DLSSize } from 'src/types/dls-size.enum';

import colors from 'src/styles/colors.module.scss';
import styles from './transactionsBudgetView.module.scss';
import TransactionTable from 'src/components/atomic/organisms/transaction/TransactionsTable';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import TransactionFilter from 'src/components/atomic/organisms/transaction/filters/TransactionFilter';
import usePaginatedTransactions from 'src/hooks/usePaginatedTransactions';
import { useSearchParams } from 'src/hooks/useSearchParams';
import { useDocumentTitleRaw } from 'src/hooks/useDocumentTitle';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import { trackClick, useTrackPage } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import TransactionsSearchInput from 'src/components/atomic/atoms/transactionsSearchInput/TransactionsSearchInput';
import { useSmoothScrollTo } from 'src/hooks/useSmoothScrollTo';
import TransactionsOverview from 'src/components/atomic/molecules/transactionsOverview/TransactionsOverview';
import { Formik } from 'formik';
import { SelectedTransactionsContext, useTransactionRows } from 'src/hooks/useSelectedTransactions';
import { TransactionModel } from 'src/types/transaction.model';
import BulkEdit from 'src/components/atomic/organisms/transaction/bulk-edit/BulkEdit';
import TransactionTableHeaderCheckbox from 'src/components/atomic/atoms/transactionsTableHeaderCheckbox/TransactionsTableHeaderCheckbox';

/**
 * The name of this component will change to TransactionsView, once the refresh is done, and will replace the
 * current TransactionsView component
 */

export default function TransactionsBudgetView(): JSX.Element {
    useDocumentTitleRaw(LiteralsService.getLiteral('Budget.titles.transactions'));
    useTrackPage(LiteralsService.getLiteral('Budget.titles.transactions'));
    const history = useHistory();
    const location = useLocation();
    const { isLoading: isLoadingPaginatedTransactions, data: paginatedTransactions } = usePaginatedTransactions();
    const filteredTransactions: TransactionModel[] = paginatedTransactions?.transactions;

    const [searchParams, setSearchParams] = useSearchParams();
    const { isMinWidthLarge } = useBreakpoints();
    const [search, setSearch] = useState<string>(searchParams.get('search'));
    useSmoothScrollTo(isLoadingPaginatedTransactions);
    const { transactionRows, setTransactionRows, setAllTransactionsSelect, selectedTransactions, showBanner } =
        useTransactionRows(filteredTransactions);

    const showHidden = searchParams.get('showHidden') === 'true';

    const handleSearch = useCallback(
        (value: string): void => {
            trackClick(location.pathname, value === '' ? adobeTags.Search.Clear : adobeTags.Search.Apply);
            if (value) {
                searchParams.set('search', value);
            } else {
                searchParams.delete('search');
                setSearch('');
            }
            searchParams.set('page', '1');
            setSearchParams(searchParams);
        },
        [location.pathname, searchParams]
    );

    const handleToggle = (): void => {
        trackClick(location.pathname, adobeTags.Transactions[showHidden ? 'ShowHiddenOff' : 'ShowHiddenOn']);
        searchParams.set('page', '1');
        searchParams.set('showHidden', showHidden ? 'false' : 'true');
        setSearchParams(searchParams);
    };

    /** Sets all the transactions rows of the table to be unchecked */
    const handleBulkEditClose = (): void => {
        trackClick(history.location.pathname, adobeTags.Transactions.Bulk.Cancel);
        setAllTransactionsSelect(false);
    };

    const bulkEditBar = showBanner && (
        <BulkEdit selectedTransactions={selectedTransactions} onClose={handleBulkEditClose} />
    );

    const SearchInput: JSX.Element = (
        <TransactionsSearchInput value={search} setSearch={setSearch} handleSearch={handleSearch} />
    );

    const searchReset = useCallback(() => handleSearch(''), [handleSearch]);

    return (
        <WideView
            hasBackgroundBanner
            showFooter
            data-cy={Selectors.SpendSmart.BUDGET_VIEW}
            title={LiteralsService.getLiteral('TransactionsView.title')}
            subtitle={LiteralsService.getLiteral('TransactionsView.subtitle')}
            hideBackButton
        >
            <Card data-cy={Selectors.SpendSmart.CARD} showBackgroundBanner>
                {isLoadingPaginatedTransactions ? (
                    <Loading lg />
                ) : (
                    <>
                        <div className={styles.topRow}>
                            <TransactionsOverview />
                            {isMinWidthLarge ? (
                                SearchInput
                            ) : (
                                <TransactionFilter tagBreak>{SearchInput}</TransactionFilter>
                            )}
                        </div>
                        {isMinWidthLarge && (
                            <div className={styles.tableControlsRow}>
                                <div>
                                    <TransactionTableHeaderCheckbox
                                        noOfSelectedTransactions={selectedTransactions.length}
                                        noOfFilteredTransactions={filteredTransactions.length}
                                        setAllTransactionsSelect={setAllTransactionsSelect}
                                    />
                                    <TransactionFilter />
                                </div>
                                <div>
                                    <ToggleSwitch
                                        defaultChecked={false}
                                        checked={showHidden}
                                        onClick={handleToggle}
                                        className={styles.toggleSwitch}
                                        data-cy={Selectors.Transactions.SHOW_IGNORED_TOGGLE}
                                    >
                                        <Typography component="p" variant={TypographyVariants.HEADING_XS}>
                                            {LiteralsService.getLiteral('TransactionsView.hiddenLabel')}
                                        </Typography>
                                    </ToggleSwitch>
                                    <ButtonPrimary
                                        className={styles.manageRulesButton}
                                        icon={<IconSetting size={DLSSize.SMALL} />}
                                        size={DLSSize.SMALL}
                                        onClick={() => {
                                            history.push(BudgetPages.TRANSACTIONS_RULES);
                                            trackClick(location.pathname, adobeTags.Transactions.ManageRules);
                                        }}
                                        data-cy={Selectors.Transactions.MANAGE_RULES}
                                    >
                                        <Typography
                                            component="p"
                                            variant={TypographyVariants.BODY}
                                            className={colors.textLight}
                                        >
                                            {LiteralsService.getLiteral('TransactionsView.manageRules')}
                                        </Typography>
                                        <GlyphRight size={DLSSize.SMALL} />
                                    </ButtonPrimary>
                                </div>
                            </div>
                        )}
                        <Formik
                            initialValues={{ transactions: filteredTransactions }}
                            enableReinitialize
                            onSubmit={() => {}}
                        >
                            <SelectedTransactionsContext.Provider
                                value={{
                                    setAllTransactionsSelect
                                }}
                            >
                                {bulkEditBar}
                                <TransactionTable
                                    transactionRows={transactionRows}
                                    setTransactionRows={setTransactionRows}
                                    totalPages={Math.ceil(
                                        paginatedTransactions?.totalLength / paginatedTransactions?.pageSize
                                    )}
                                    searchReset={searchReset}
                                />
                            </SelectedTransactionsContext.Provider>
                        </Formik>
                    </>
                )}
            </Card>
        </WideView>
    );
}
